import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import personIcon from "../../src/assests/personIcon.svg";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../providers/AuthProvider";
import BrandedLogo from "./branded-logo";
import SideBarData from "../utils/data/sideBar";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import clsx from "clsx";
import UseChat from "../utils/useChat";
import ThemeToggle from "./theme-toggle";
import { faAngleLeft, faRightFromBracket, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useSidebar } from "../store/use-sidebar";
import { useMediaQuery } from "usehooks-ts";

/** Hover label shown next to icons while the sidebar is collapsed. */
const tooltipClass =
  "pointer-events-none absolute left-full ml-3 z-50 rounded-lg bg-surface-elevated border border-edge px-3 py-1.5 text-xs text-text-primary shadow-card opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap";

export default function LeftSidebar() {
  const { unReadMessageCount } = UseChat();
  const { user, HandleUserLogOut } = useContext(AuthContext);
  const { collapsed: desktopCollapsed, toggleSidebar, setMobileOpen } = useSidebar();
  // Mobile renders the sidebar as an off-canvas drawer: always expanded, button closes it
  const isMobile = useMediaQuery("(max-width: 768px)");
  const collapsed = isMobile ? false : desktopCollapsed;
  const closeMobile = () => {
    if (isMobile) setMobileOpen(false);
  };

  const isActive = (path: string) => {
    return window.location.pathname === path;
  };

  return (
    <div className="relative bg-surface h-full animate-fade-in-slow">
      <div className="sticky top-0 h-screen smallTablet:h-[100dvh] w-full border-r border-edge flex flex-col">
        {/* ----------------------------------- [ HEADER ] ----------------------------------- */}
        <div
          className={clsx(
            "flex items-center border-b border-edge-faint transition-all duration-300 shrink-0",
            collapsed ? "justify-center px-3 pt-5 pb-4" : "justify-between px-4 pt-5 pb-4"
          )}
        >
          <div
            className={clsx(
              "flex items-center overflow-hidden transition-all duration-500 ease-out",
              collapsed ? "w-0 opacity-0 -translate-x-2" : "w-full opacity-100 translate-x-0"
            )}
          >
            <BrandedLogo className="w-[104px] shrink-0 opacity-95" />
          </div>

          <button
            type="button"
            onClick={isMobile ? closeMobile : toggleSidebar}
            aria-label={isMobile ? "Close menu" : "Toggle sidebar"}
            title={isMobile ? "Close menu" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray hover:text-text-primary hover:border-indigo-400/40 hover:shadow-glow-sm transition-all duration-200 cursor-pointer"
          >
            <FontAwesomeIcon
              icon={isMobile ? faXmark : faAngleLeft}
              className={clsx(
                "text-xs transition-transform duration-300",
                collapsed && "rotate-180"
              )}
            />
          </button>
        </div>

        {/* ------------------------------------ [ NAV ] ------------------------------------ */}
        <ul className="font-medium flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-2">
          {SideBarData.map((item) => {
            if (item.label === "Agents" && user?.type === "agent") {
              return null;
            }

            const active = isActive(item.route);
            const isChat = item.label === "Chats";

            return (
              <div
                className={item.label === "My Customers" ? "mt-10" : ""}
                key={item.route}
              >
                <li
                  className={clsx(
                    "group relative flex gap-3 py-3 items-center transition-all duration-200 hover:bg-active-background hover:border-r-[3px] hover:border-blue",
                    collapsed ? "px-0 justify-center" : "px-6",
                    active && "border-blue bg-active-background border-r-[3px]"
                  )}
                >
                  <div className="w-[26px] flex items-center justify-center shrink-0 relative">
                    <FontAwesomeIcon
                      icon={item.icon as IconProp}
                      className={clsx(
                        "text-[17px] transition-colors duration-200",
                        active
                          ? "text-blue"
                          : "text-light-gray group-hover:text-blue"
                      )}
                    />
                    {collapsed && isChat && unReadMessageCount ? (
                      <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-gradient-to-r from-accent to-accent-bright shadow-glow-sm" />
                    ) : null}
                  </div>

                  {!collapsed ? (
                    <Link
                      to={item.route}
                      onClick={closeMobile}
                      className={clsx(
                        "flex-1 font-medium text-nowrap text-[15px] transition-colors duration-200",
                        active
                          ? "text-text-primary"
                          : "text-light-gray group-hover:text-text-primary"
                      )}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Link
                      to={item.route}
                      onClick={closeMobile}
                      aria-label={item.label}
                      className="absolute inset-0"
                    />
                  )}

                  {!collapsed && isChat && unReadMessageCount ? (
                    <div className="h-[22px] min-w-[25px] px-1 flex items-center text-[12px] justify-center bg-gradient-to-r from-accent to-accent-bright rounded-full text-white shadow-glow-sm">
                      {unReadMessageCount}
                    </div>
                  ) : null}

                  {collapsed ? (
                    <span className={clsx(tooltipClass, "smallLaptop:hidden")}>
                      {item.label}
                    </span>
                  ) : null}
                </li>

                {item?.subData?.length && !collapsed ? (
                  <ul className="ml-14 flex flex-col">
                    {item.subData.map((subItem) => (
                      <Link
                        key={subItem.route}
                        to={subItem.route}
                        onClick={closeMobile}
                        className={clsx(
                          "my-3 cursor-pointer w-full font-medium text-nowrap text-[14px] text-light-gray hover:text-text-primary transition-colors duration-200",
                          isActive(subItem.route) && "!text-blue"
                        )}
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </ul>

        {/* ----------------------------------- [ FOOTER ] ----------------------------------- */}
        <div className="shrink-0">
          <ThemeToggle collapsed={collapsed} />
          <button
            type="button"
            onClick={HandleUserLogOut}
            title={collapsed ? "Log out" : undefined}
            className={clsx(
              "group/logout relative w-full cursor-pointer bg-surface-elevated border-t border-edge py-3 flex items-center gap-3 transition-colors duration-300 hover:bg-active-background text-left",
              collapsed ? "px-2 justify-center" : "px-5 justify-between"
            )}
          >
            <div
              className={clsx(
                "flex items-center gap-3 min-w-0",
                collapsed && "justify-center"
              )}
            >
              <img
                className={clsx(
                  "rounded-full ring-2 ring-indigo-500/30 shrink-0 transition-all duration-300",
                  collapsed ? "w-9 h-9" : "w-11 h-11"
                )}
                src={personIcon}
                alt=""
              />
              {!collapsed ? (
                <div className="flex flex-col min-w-0">
                  <p className="font-medium text-[15px] text-text-primary truncate">
                    {user?.name}
                  </p>
                  <p className="font-medium text-xs text-light-gray capitalize truncate">
                    {user?.type?.replace("-", " ")}
                  </p>
                </div>
              ) : null}
            </div>

            {!collapsed ? (
              <FontAwesomeIcon
                icon={faRightFromBracket}
                className="text-light-gray group-hover/logout:text-blue text-sm shrink-0 transition-colors"
                title="Log out"
              />
            ) : null}
          </button>
        </div>
      </div>
    </div>
  );
}
